import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useQuery } from "convex/react";
import { mapEntityTypeToBackend } from "../utils/field-utils";

// ============================================================================
// Clarification Feature - Data Hooks
// ============================================================================
// Hooks for fetching clarification request data from Convex backend

/**
 * Fetch all clarification requests for a project.
 */
export function useClarificationRequests(projectId: Id<"projects"> | null) {
  return useQuery(api.clarifications.listByProject, projectId ? { projectId } : "skip");
}

/**
 * Fetch all clarification requests for a project decorated with per-user read
 * state and a reply count. Used by the project-wide clarifications page.
 */
export function useClarificationRequestsWithReadState(projectId: Id<"projects"> | null) {
  return useQuery(
    api.clarifications.listByProjectWithReadState,
    projectId ? { projectId } : "skip"
  );
}

/**
 * Fetch all clarifications for a specific entity.
 * Automatically maps frontend entity type strings to backend format.
 */
export function useClarificationsByEntity(
  projectId: Id<"projects"> | null,
  entityType: string | null,
  entityId: string | null
) {
  const shouldFetch = projectId && entityType && entityId;
  return useQuery(
    api.clarifications.listByEntity,
    shouldFetch
      ? {
          projectId,
          subjectType: mapEntityTypeToBackend(entityType),
          subjectId: entityId,
        }
      : "skip"
  );
}

/**
 * Fetch open clarification counts per entity for a project.
 * Returns a map like { "kpi:abc123": 2, "dataset:def456": 1 }.
 * Used by the project tree to show attention indicators.
 */
export function useProjectAttentionMap(projectId: Id<"projects"> | null) {
  return useQuery(api.clarifications.getProjectAttentionMap, projectId ? { projectId } : "skip");
}

/**
 * Fetch messages for a specific clarification request.
 */
export function useClarificationRequestMessages(
  clarificationRequestId: Id<"clarificationRequests"> | null
) {
  return useQuery(
    api.clarifications.listMessages,
    clarificationRequestId ? { clarificationRequestId } : "skip"
  );
}

/**
 * Fetch the append-only owner assignment history for a clarification request.
 * Returns rows ascending by creation time; each row carries resolved display
 * names so the UI can render "X assigned Y · date" without N+1 user lookups.
 */
export function useClarificationAssignmentHistory(
  clarificationRequestId: Id<"clarificationRequests"> | null
) {
  return useQuery(
    api.clarifications.listAssignmentHistory,
    clarificationRequestId ? { clarificationRequestId } : "skip"
  );
}

/**
 * Fetch a single clarification request by ID.
 */
export function useClarificationRequest(
  clarificationRequestId: Id<"clarificationRequests"> | null
) {
  return useQuery(
    api.clarifications.getById,
    clarificationRequestId ? { clarificationRequestId } : "skip"
  );
}

/**
 * Fetch the minimal Claudia triage status needed for the live Working row.
 */
export function useClarificationTriageStatus(
  clarificationRequestId: Id<"clarificationRequests"> | null
) {
  return useQuery(
    api.clarifications.getTriageStatus,
    clarificationRequestId ? { clarificationRequestId } : "skip"
  );
}

/**
 * Fetch all users in an organization.
 * Used for the owner selector dropdown in the create clarification form.
 */
export function useOrganizationUsers(orgId: Id<"orgs"> | null) {
  return useQuery(api.users.listByOrganization, orgId ? { orgId } : "skip");
}
